<template>
  <div class="custom-input-text no-zoom">
    <textarea v-if="isTextarea" :id="id" :value="displayValue" @input="handleInput" @keyup="onKeyUp" @focus="onFocus"
      @blur="onBlur" :maxlength="maxLength" :autocomplete="autocomplete" :name="name" :class="{
        'has-value': modelValue,
        'valid-input': validationStatus === 'valid',
        'invalid-input': validationStatus === 'invalid',
        'shake-animation': isShaking
      }"></textarea>
    <input v-else :id="id" :type="type" :value="displayValue" @input="handleInput" @keyup="onKeyUp" @focus="onFocus"
      @blur="onBlur" :maxlength="maxLength" :autocomplete="autocomplete" :name="name" :class="{
        'has-value': modelValue,
        'valid-input': validationStatus === 'valid',
        'invalid-input': validationStatus === 'invalid',
        'shake-animation': isShaking
      }" />
    <label :for="id" :class="{ 'is-floating': isFloating || modelValue }">{{ label }}</label>
    <span v-if="type === 'date' && !isFloating && !modelValue" class="placeholder">dd/mm/yyyy</span>

    <!-- Font Awesome Icons -->

  </div>
</template>

<script>
/**
 * @component CustomInputText
 * @description Componente de entrada de texto personalizado con soporte para diferentes tipos de entrada,
 * máscaras, formateo de moneda, validación y estados visuales.
 */
export default {
  name: 'CustomInputText',
  props: {
    /**
     * ID único del input para asociarlo con su etiqueta
     */
    id: {
      type: String,
      required: true
    },
    /**
     * Valor del input (v-model)
     */
    modelValue: {
      type: [String, Number],
      required: true
    },
    /**
     * Tipo de input (text, number, date, etc)
     */
    type: {
      type: String,
      default: 'text'
    },
    /**
     * Etiqueta que se muestra para el campo
     */
    label: {
      type: String,
      required: true
    },
    /**
     * Máscara para formatear la entrada (9: dígito, X: alfanumérico, A: alfabético)
     */
    mask: {
      type: String,
      default: ''
    },
    /**
     * Modo de funcionamiento específico (ej: 'currency')
     */
    mode: {
      type: String,
      default: ''
    },
    /**
     * Código de moneda para formatear valores monetarios
     */
    currency: {
      type: String,
      default: ''
    },
    /**
     * Configuración regional para formateo
     */
    locale: {
      type: String,
      default: 'en-US'
    },
    /**
     * Prefijo para mostrar antes del valor
     */
    prefix: {
      type: String,
      default: ''
    },
    /**
     * Sufijo para mostrar después del valor
     */
    suffix: {
      type: String,
      default: ''
    },
    /**
     * Transformación automática de mayúsculas/minúsculas
     * @values uppercase, lowercase, none
     */
    textCase: {
      type: String,
      default: 'uppercase',
      validator: (value) => ['uppercase', 'lowercase', 'none'].includes(value)
    },
    /**
     * Tipo de validación a aplicar
     * @values email, phone, ''
     */
    validationType: {
      type: String,
      default: '',
      validator: (value) => ['email', 'phone', ''].includes(value)
    },
    /**
     * Biblioteca de iconos a utilizar
     * @values font-awesome, material, prime, bootstrap, ''
     */
    useIcons: {
      type: String,
      default: 'prime',
      validator: (value) => ['font-awesome', 'material', 'prime', 'bootstrap', ''].includes(value)
    },
    /**
     * Define si el input debe ser un textarea
     */
    isTextarea: {
      type: Boolean,
      default: false
    },
    /**
     * Valor para el atributo autocomplete del input
     */
    autocomplete: {
      type: String,
      default: 'off'
    },
    /**
     * Valor para el atributo name del input
     */
    name: {
      type: String,
      default: ''
    },
    /**
     * Longitud máxima permitida para la entrada
     * Esta propiedad tiene prioridad sobre la longitud derivada de la máscara
     */
    length: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      isFloating: false,
      rawValue: '', // Track the raw unmasked value
      validationStatus: '', // Track validation status
      isShaking: false, // Track if shake animation is active
      isEditing: false // Track if user is currently editing
    };
  },
  computed: {
    /**
     * Calcula el valor formateado a mostrar en el input
     * @returns {string} - Valor formateado para mostrar
     */
    displayValue() {
      try {
        // Si el usuario está editando un valor de moneda, mostrar valor sin prefijo/sufijo
        if (this.mode === 'currency' && this.isEditing) {
          return this.modelValue || '';
        }

        // Si tenemos una moneda y modo de moneda, formateamos el valor
        if (this.mode === 'currency' && this.currency && this.modelValue) {
          // Verificar si el valor termina con punto decimal (usuario está ingresando parte decimal)
          if (this.modelValue.toString().endsWith('.')) {
            // Mantener el punto decimal visible mientras se escribe
            return this.modelValue.toString();
          }

          const numValue = parseFloat(this.modelValue);
          if (!isNaN(numValue)) {
            const formatted = new Intl.NumberFormat(this.locale, {
              style: 'currency',
              currency: this.currency,
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(numValue);

            // No añadir prefijo/sufijo durante la edición para evitar problemas de entrada
            return formatted;
          }
        }

        // Si tenemos una máscara, aplicamos el enmascaramiento
        if (this.mask && this.modelValue) {
          return this.applyMask(this.modelValue);
        }

        // Valor sin formato
        return this.modelValue ? `${this.prefix}${this.modelValue}${this.suffix}` : '';
      } catch (error) {

        return this.modelValue || '';
      }
    },

    /**
     * Calcula la longitud máxima permitida para la entrada
     * @returns {number|null} - Longitud máxima de caracteres o null si no hay límite
     */
    maxLength() {
      // Si se proporciona length explícitamente, tiene prioridad
      if (this.length !== null) {
        return this.length;
      }

      try {
        if (!this.mask) return null;

        // Para máscaras numéricas, limitamos al número de '9's
        if (/[0-9]/.test(this.mask)) {
          return (this.mask.match(/[0-9]/g) || []).length;
        }

        // Para máscaras alfanuméricas, limitamos por la longitud de 'X's
        if (this.mask.includes('X')) {
          return (this.mask.match(/X/g) || []).length;
        }

        // Para máscaras alfabéticas, limitamos por la longitud de 'A's
        if (this.mask.includes('A')) {
          return (this.mask.match(/A/g) || []).length;
        }

        // Para otros tipos de máscaras, usamos la longitud de la máscara
        return this.mask.length;
      } catch (error) {

        return null;
      }
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        // Mantener rawValue sincronizado con modelValue
        try {
          if (newVal !== undefined) {
            this.rawValue = newVal;

            // Aplicar restricción de longitud cuando cambia el valor del modelo
            if (this.length !== null && typeof newVal === 'string' && newVal.length > this.length) {
              this.$emit('update:modelValue', newVal.substring(0, this.length));
            }
          }
        } catch (error) {

        }
      }
    }
  },
  methods: {
    /**
     * Maneja el evento de focus en el input
     */
    onFocus() {
      try {
        this.isFloating = true;
        if (this.mode === 'currency') {
          this.isEditing = true;
        }
      } catch (error) {

      }
    },

    /**
     * Maneja el evento de pérdida de focus en el input
     */
    onBlur() {
      try {
        if (!this.modelValue) {
          this.isFloating = false;
        }
        this.isEditing = false;
        this.validateInput();
      } catch (error) {

      }
    },

    /**
     * Maneja el evento keyup en el input
     */
    onKeyUp() {
      try {
        // Validar email en keyup
        if (this.validationType === 'email') {
          this.validateInput();
        }

        // Aplicar formato de moneda en keyup cuando está en modo moneda
        if (this.mode === 'currency' && this.modelValue) {
          this.formatCurrencyOnKeyup();
        }
      } catch (error) {

      }
    },

    /**
     * Formatea la entrada de moneda durante la escritura
     */
    formatCurrencyOnKeyup() {
      try {
        // No formatear si actualmente se está escribiendo un punto decimal
        if (this.modelValue.toString().endsWith('.')) {
          return;
        }

        // Verificar si es un número válido para formatear
        const numValue = parseFloat(this.modelValue);
        if (!isNaN(numValue)) {
          // Almacenar la posición del cursor para restaurarla después
          const input = document.getElementById(this.id);

          // Solo intentar gestionar la posición del cursor si el input existe y soporta selección
          if (input && input.type !== 'number') {
            const cursorPosition = input ? input.selectionStart : 0;
            const oldLength = input ? input.value.length : 0;

            this.$nextTick(() => {
              // Intentar restaurar la posición del cursor después del formateo
              if (input) {
                // Ajustar la posición del cursor basado en el cambio de longitud
                const newPosition = Math.min(
                  cursorPosition + (input.value.length - oldLength),
                  input.value.length
                );
                input.setSelectionRange(newPosition, newPosition);
              }
            });
          }
        }
      } catch (error) {

      }
    },

    /**
     * Valida la entrada según el tipo de validación configurado
     */
    validateInput() {
      try {
        const previousStatus = this.validationStatus;

        if (this.validationType === 'email') {
          // Solo validar email si hay algún contenido (para evitar estado inválido cuando recién se empieza a escribir)
          if (this.modelValue && this.modelValue.length > 0) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            this.validationStatus = emailPattern.test(this.modelValue) ? 'valid' : 'invalid';
          } else {
            // Input vacío no debe marcarse como inválido
            this.validationStatus = '';
          }
        } else if (this.validationType === 'phone') {
          const phonePattern = /^\d{10}$/; // Patrón de ejemplo para un número de teléfono de 10 dígitos
          this.validationStatus = phonePattern.test(this.modelValue) ? 'valid' : 'invalid';
        } else {
          this.validationStatus = '';
        }

        // Activar la animación shake cuando se transiciona a estado inválido
        if (this.validationStatus === 'invalid' && (previousStatus !== 'invalid' || !previousStatus)) {
          this.shakeInput();
        }
      } catch (error) {

      }
    },

    /**
     * Activa la animación de shake para inputs inválidos
     */
    shakeInput() {
      try {
        this.isShaking = true;

        // Remover la clase después de que la animación se completa para permitir que se active de nuevo
        setTimeout(() => {
          this.isShaking = false;
        }, 820); // Duración de la animación + un poco extra
      } catch (error) {

      }
    },

    /**
     * Aplica transformación de mayúsculas/minúsculas según configuración
     * @param {string} value - Valor a transformar
     * @returns {string} - Valor transformado
     */
    applyTextCase(value) {
      try {
        if (!value) return value;

        switch (this.textCase) {
          case 'uppercase':
            return value.toString().toUpperCase();
          case 'lowercase':
            return value.toString().toLowerCase();
          default:
            return value;
        }
      } catch (error) {

        return value;
      }
    },

    /**
     * Maneja el evento de input
     * @param {Event} event - Evento de input
     */
    handleInput(event) {
      try {
        let inputValue = event.target.value;

        // Siempre aplicar la restricción de longitud primero
        if (this.length !== null) {
          // Comprobar si el valor supera la longitud máxima
          if (inputValue.length > this.length) {
            inputValue = inputValue.substring(0, this.length);

            // Actualizar directamente el valor del elemento de entrada para la retroalimentación visual inmediata
            event.target.value = inputValue;
          }
        }

        // Guardar la posición del cursor antes de cualquier modificación
        const cursorPosition = event.target.selectionStart;

        // Caso especial para campos numéricos con longitud explícita
        if (this.type === 'number' && this.length !== null) {
          // Filtrar solo dígitos
          const digitsOnly = inputValue.replace(/\D/g, '');
          // Limitar a la longitud especificada
          const limitedValue = digitsOnly.substring(0, this.length);
          // Emitir el valor actualizado
          this.$emit('update:modelValue', limitedValue);
          return;
        }

        // Caso especial para longitud máxima explícita (sin máscara)
        if (this.length !== null && !this.mask) {
          const limitedValue = inputValue.substring(0, this.length);
          const formattedValue = this.applyTextCase(limitedValue);
          this.$emit('update:modelValue', formattedValue);
          return;
        }

        // Manejo especial para modo moneda
        if (this.mode === 'currency') {
          // Bloquear todas las entradas no numéricas excepto el separador decimal
          const sanitizedValue = this.sanitizeCurrencyInput(inputValue);

          // Actualizar el input con el valor sanitizado
          event.target.value = sanitizedValue;

          // Emitir el valor procesado
          this.$emit('update:modelValue', sanitizedValue);
          return;
        }

        // Eliminar prefijos y sufijos si existen
        if (this.prefix && inputValue.startsWith(this.prefix)) {
          inputValue = inputValue.substring(this.prefix.length);
        }
        if (this.suffix && inputValue.endsWith(this.suffix)) {
          inputValue = inputValue.substring(0, inputValue.length - this.suffix.length);
        }

        // Si estamos en modo moneda, manejamos de forma especial
        if (this.mode === 'currency') {
          // Manejo mejorado de moneda
          // 1. Eliminar todos los caracteres no numéricos excepto el primer punto decimal
          let processedValue = '';
          let decimalFound = false;

          for (let i = 0; i < inputValue.length; i++) {
            const char = inputValue[i];
            // Permitir dígitos
            if (/\d/.test(char)) {
              processedValue += char;
            }
            // Permitir el primer punto decimal (ya sea '.' o ',')
            else if ((char === '.' || char === ',') && !decimalFound) {
              processedValue += '.'; // Siempre usar punto como separador decimal internamente
              decimalFound = true;
            }
          }

          // 2. Manejar el caso cuando el usuario está ingresando parte decimal
          if (inputValue.endsWith('.') || inputValue.endsWith(',')) {
            processedValue += '.';
          }

          // 3. Asegurarnos de no tener solo un punto decimal
          if (processedValue === '.') {
            processedValue = '0.';
          }

          // Parsear a número si es un número completo
          if (!/\.$/.test(processedValue)) {
            const numValue = parseFloat(processedValue);
            if (!isNaN(numValue)) {
              processedValue = numValue.toString();
            }
          }

          this.$emit('update:modelValue', processedValue);
          return;
        }

        // Caso especial para máscara de 4 dígitos (como año)
        if (this.mask === '9999') {
          // Eliminar cualquier carácter no numérico
          const digitsOnly = inputValue.replace(/\D/g, '');
          // Limitar a 4 dígitos
          const limitedValue = digitsOnly.substring(0, 4);
          // Emitir el valor actualizado
          this.$emit('update:modelValue', limitedValue);
          return;
        }

        // Si no hay máscara, simplemente actualizamos el valor aplicando el formato de texto
        if (!this.mask) {
          const formattedValue = this.applyTextCase(inputValue);
          this.$emit('update:modelValue', formattedValue);
          return;
        }

        // Lógica para máscaras numéricas
        if (this.mask.includes('9')) {
          const digits = inputValue.replace(/[^0-9]/g, '');
          const maxLength = (this.mask.match(/9/g) || []).length;
          const limitedValue = digits.substring(0, maxLength);

          this.$emit('update:modelValue', limitedValue);
          this.rawValue = limitedValue;

          setTimeout(() => {
            if (limitedValue.length > 0) {
              event.target.value = this.applyMask(limitedValue);
              this.setCaretPosition(event.target, limitedValue.length);
            } else {
              event.target.value = '';
            }
          }, 0);
        }
        // Lógica para máscaras alfanuméricas (letras y números)
        else if (this.mask.includes('X')) {
          // Guardar la posición actual antes de modificar el valor
          const currentPosition = cursorPosition;

          // Primero eliminamos caracteres no alfanuméricos
          let alphanumeric = inputValue.replace(/[^a-zA-Z0-9]/g, '');
          // Luego aplicamos la transformación de caso según la propiedad textCase
          alphanumeric = this.applyTextCase(alphanumeric);

          const maxLength = (this.mask.match(/X/g) || []).length;
          const limitedValue = alphanumeric.substring(0, maxLength);

          this.$emit('update:modelValue', limitedValue);
          this.rawValue = limitedValue;

          setTimeout(() => {
            if (limitedValue.length > 0) {
              // Actualizar el valor sin cambiar la posición del cursor
              const oldValue = event.target.value;
              event.target.value = this.applyMask(limitedValue);

              // Calcular la posición ajustada del cursor
              let adjustedPosition = currentPosition;

              // Si estamos escribiendo (agregando caracteres), mantener el cursor después del carácter insertado
              if (event.inputType === 'insertText' && currentPosition > 0) {
                adjustedPosition = currentPosition;
              }
              // Si estamos eliminando caracteres, mantener el cursor en la posición actual
              else if (event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward') {
                adjustedPosition = Math.min(currentPosition, event.target.value.length);
              }

              // Establecer la posición del cursor preservando donde estaba escribiendo el usuario
              this.$nextTick(() => {
                if (event.target.setSelectionRange) {
                  event.target.focus();
                  event.target.setSelectionRange(adjustedPosition, adjustedPosition);
                }
              });
            } else {
              event.target.value = '';
            }
          }, 0);
        }
        // Lógica para máscaras alfabéticas (solo letras)
        else if (this.mask.includes('A')) {
          // Primero eliminamos caracteres no alfabéticos
          let alpha = inputValue.replace(/[^a-zA-Z]/g, '');
          // Luego aplicamos la transformación de caso según la propiedad textCase
          alpha = this.applyTextCase(alpha);

          const maxLength = (this.mask.match(/A/g) || []).length;
          const limitedValue = alpha.substring(0, maxLength);

          this.$emit('update:modelValue', limitedValue);
          this.rawValue = limitedValue;

          setTimeout(() => {
            if (limitedValue.length > 0) {
              event.target.value = this.applyMask(limitedValue);
              this.setCaretPosition(event.target, limitedValue.length);
            } else {
              event.target.value = '';
            }
          }, 0);
        }
        else {
          // Para otros tipos de máscaras, aplicamos la transformación de caso
          const formattedValue = this.applyTextCase(inputValue);
          this.$emit('update:modelValue', formattedValue);
        }
      } catch (error) {

      }
    },

    /**
     * Establece la posición del cursor en el input
     * @param {HTMLInputElement} input - Elemento input
     * @param {number} pos - Posición deseada del cursor
     */
    setCaretPosition(input, pos) {
      try {
        // Obtener la posición del cursor actual antes de cualquier modificación
        const currentPos = input.selectionStart;

        // Omitir posicionamiento para tipos de input que no soportan selección
        if (input.type === 'number') {
          return;
        }

        // Usar la posición actual del cursor cuando está disponible y es válida
        const position = (currentPos !== null && currentPos >= 0) ? currentPos : Math.min(pos, input.value.length);

        // Establecer la posición del cursor
        if (input.setSelectionRange) {
          input.focus();
          input.setSelectionRange(position, position);
        } else if (input.createTextRange) {
          const range = input.createTextRange();
          range.collapse(true);
          range.moveEnd('character', position);
          range.moveStart('character', position);
          range.select();
        }
      } catch (error) {

      }
    },

    /**
     * Aplica la máscara al valor
     * @param {string} value - Valor a enmascarar
     * @returns {string} - Valor enmascarado
     */
    applyMask(value) {
      try {
        if (!value || !this.mask) return value;

        // Enmascaramiento para entradas numéricas
        if (this.mask.includes('9')) {
          return this.applyNumericMask(value);
        }
        // Enmascaramiento para entradas alfanuméricas
        else if (this.mask.includes('X')) {
          return this.applyAlphanumericMask(value);
        }
        // Enmascaramiento para entradas alfabéticas
        else if (this.mask.includes('A')) {
          return this.applyAlphabeticMask(value);
        }

        // Para otros tipos de máscaras
        return value;
      } catch (error) {

        return value;
      }
    },

    /**
     * Aplica máscara numérica (9)
     * @param {string} value - Valor a enmascarar
     * @returns {string} - Valor enmascarado
     */
    applyNumericMask(value) {
      try {
        let result = this.mask;
        let valueIndex = 0;

        // Mejorar la lógica de máscara numérica para aceptar todos los dígitos
        // incluyendo el número 9
        for (let i = 0; i < result.length && valueIndex < value.length; i++) {
          // Reemplazar '9' con cualquier dígito (0-9)
          if (result[i] === '9') {
            const digit = value[valueIndex];
            // Asegurarnos de que el carácter sea un dígito válido antes de insertarlo
            if (/^\d$/.test(digit)) {
              result = result.substring(0, i) + digit + result.substring(i + 1);
              valueIndex++;
            }
          }
        }

        // Limpiar los marcadores de posición '9' no utilizados
        result = result.replace(/9/g, '');

        return result;
      } catch (error) {
        
        return value;
      }
    },

    /**
     * Aplica máscara alfanumérica (X)
     * @param {string} value - Valor a enmascarar
     * @returns {string} - Valor enmascarado
     */
    applyAlphanumericMask(value) {
      try {
        let result = this.mask;
        let valueIndex = 0;

        // Aseguramos que el valor esté en el caso correcto antes de aplicar la máscara
        value = this.applyTextCase(value);

        for (let i = 0; i < result.length && valueIndex < value.length; i++) {
          if (result[i] === 'X') {
            result = result.substring(0, i) + value[valueIndex++] + result.substring(i + 1);
          }
        }

        // Asegurarnos de no mostrar los marcadores 'X' restantes
        return result.replace(/X/g, '');
      } catch (error) {

        return value;
      }
    },

    /**
     * Aplica máscara alfabética (A)
     * @param {string} value - Valor a enmascarar
     * @returns {string} - Valor enmascarado
     */
    applyAlphabeticMask(value) {
      try {
        let result = this.mask;
        let valueIndex = 0;

        // Aseguramos que el valor esté en el caso correcto antes de aplicar la máscara
        value = this.applyTextCase(value);

        for (let i = 0; i < result.length && valueIndex < value.length; i++) {
          if (result[i] === 'A') {
            result = result.substring(0, i) + value[valueIndex++] + result.substring(i + 1);
          }
        }

        return result.replace(/A/g, '');
      } catch (error) {

        return value;
      }
    },

    /**
     * Sanitiza la entrada de moneda
     * @param {string} value - Valor a sanitizar
     * @returns {string} - Valor sanitizado
     */
    sanitizeCurrencyInput(value) {
      try {
        // Remove all non-numeric characters except decimal separator
        let processedValue = '';
        let decimalFound = false;

        // First remove all currency formatting characters (dollar signs, commas, etc.)
        value = value.replace(/[$,\s]/g, '');

        for (let i = 0; i < value.length; i++) {
          const char = value[i];
          // Allow digits
          if (/\d/.test(char)) {
            processedValue += char;
          }
          // Allow only one decimal point (either '.' or ',')
          else if ((char === '.' || char === ',') && !decimalFound) {
            processedValue += '.'; // Always use period as decimal separator internally
            decimalFound = true;
          }
          // All other characters (letters, special chars) are ignored
        }

        // If ends with decimal, preserve it
        if (value.endsWith('.') || value.endsWith(',')) {
          if (!processedValue.includes('.')) {
            processedValue += '.';
          }
        }

        return processedValue;
      } catch (error) {

        return value;
      }
    }
  }
};
</script>

<style scoped>
.custom-input-text {
  position: relative;
  margin-top: 20px;
}

.custom-input-text input:focus {
  outline: none;
  border-color: var(--input-hover-border-color);
}

.custom-input-text input:hover {
  outline: none;
  border-color: var(--input-hover-border-color);
}

.custom-input-text input {
  width: 100%;
  padding: 0.5rem;
  height: 38px;
  color: var(--label-color);
  border-radius: 2px;
  background-color: var(--button-hover-background-color);
  border: 1px solid var(--input-border-color);
  transition: border-color 0.3s ease;
}

.custom-input-text textarea {
  width: 100%;
  padding: 0.5rem;
  height: auto;
  color: var(--label-color);
  border-radius: 2px;
  background-color: var(--button-hover-background-color);
  border: 1px solid var(--input-border-color);
  transition: border-color 0.3s ease;
  resize: vertical;
  /* Allow vertical resizing */
}

.custom-input-text label {
  position: absolute;
  top: 50%;
  left: 0.5rem;
  transform: translateY(-50%);
  color: var(--label-color);
  transition: all 0.3s ease;
  pointer-events: none;
}

.custom-input-text input:focus+label,
.custom-input-text input.has-value+label,
.custom-input-text label.is-floating {
  top: -0.75rem;
  left: 0.4rem;
  padding: 0 0.25rem;
  color: var(--label-color);
  font-size: 0.75rem;
}

.custom-input-text .placeholder {
  position: absolute;
  top: 50%;
  left: 0.5rem;
  transform: translateY(-50%);
  color: var(--input-border-color);
  pointer-events: none;
}

.icon {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  font-size: 1.25rem;
}

.valid {
  color: green;
}

.invalid-icon {
  color: red;
}

/* For Material Icons */
.material-icons {
  font-family: 'Material Icons';
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;

  -webkit-font-smoothing: antialiased;
}

/* Validation border styles */
.custom-input-text input.valid-input {
  border-color: green;
  border-width: 1px;
}

.custom-input-text input.invalid-input {
  border-color: red;
  border-width: 1px;
}

/* Shake animation */
@keyframes shake {
  0% {
    transform: translateX(0);
  }

  10% {
    transform: translateX(-10px);
  }

  20% {
    transform: translateX(10px);
  }

  30% {
    transform: translateX(-8px);
  }

  40% {
    transform: translateX(8px);
  }

  50% {
    transform: translateX(-5px);
  }

  60% {
    transform: translateX(5px);
  }

  70% {
    transform: translateX(-3px);
  }

  80% {
    transform: translateX(3px);
  }

  90% {
    transform: translateX(-1px);
  }

  100% {
    transform: translateX(0);
  }
}

.shake-animation {
  animation: shake 0.8s cubic-bezier(.36, .07, .19, .97) both;
}
</style>
